import { Component, DestroyRef, inject } from '@angular/core';
import { parseZone } from 'moment';
import { Photographer, PhotographerBrand, PhotographerCategory } from '../../../../models/common/photographer.models';
import { Brand } from '../../../../models/common/brand.model';
import { Category } from '../../../../models/common/category.model';
import { PhotographerStatus } from '../../../../models/common/photographer-status.model';
import { PhotographerStatusEnum } from '../../../../models/common/photographer-status.enum';
import { UtilitiesService } from '../../../../shared/services/utilities.service';
import { GraphQLService } from '../../../../services/graph-ql.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PhotographerQueries, SharedQueries } from '../../../../models/shared/enums/get-query';
import { map } from 'rxjs';
import { AppService } from '../../../../app.service';
import { WrapperResultGraphQL } from '../../../../models/shared/types/wrapper-result-graphql.type';
import { MutationQuery } from '../../../../models/shared/enums/mutation-query.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';

@Component({
	standalone: false,
	selector: 'bt-company-photographer-detail',
	templateUrl: './company-photographer-detail.component.html',
	styleUrls: ['./company-photographer-detail.component.scss']
})
export class CompanyPhotographerDetailComponent {
	readonly #utilitiesService = inject(UtilitiesService);
	readonly #graphQLService = inject(GraphQLService);
	readonly #destroyRef = inject(DestroyRef);
	readonly #appService = inject(AppService);
	readonly #activatedRoute = inject(ActivatedRoute);
	readonly #router = inject(Router);

	form: FormGroup;
	photographer: Photographer;
	brands: { data: Array<Brand>; selected?: Brand } = {
		data: []
	};
	categories: { data: Array<Category>; selected?: Category } = {
		data: []
	};
	status: { data: Array<PhotographerStatus>; selected?: PhotographerStatus } = {
		data: []
	};
	readonly statusIdToSetHiredData = PhotographerStatusEnum.Hired;

	constructor() {
		this.getPhotographerStatus();
		const photographerId = this.#activatedRoute.snapshot.params['photographerId'];
		this.getPhotographerById(Number(photographerId));
	}

	onSelectBrand(b: Brand) {
		this.brands.selected = b;
		!this.brands.selected ? (this.categories.selected = null) : this.getCategories();
	}

	onSelectStatus(s: PhotographerStatus) {
		this.status.selected = s;
		this.photographer.statusId = s ? s.id : undefined;
		this.photographer.statusName = s ? s.name : undefined;
	}

	onSelectCategory(c: Category) {
		this.categories.selected = c;
	}

	removeBrand(b: PhotographerBrand, c?: PhotographerCategory) {
		this.brands.selected = undefined;
		this.photographer.brands = this.photographer.brands.filter((x) => {
			let flag = true;
			if (!c) {
				flag = x.brandId !== b.brandId;
			} else if (x.brandId === b.brandId) {
				b.categories = b.categories.filter((cat) => c.categoryId !== cat.categoryId);
				flag = !!b.categories.length;
			}
			return flag;
		});
	}

	addBrand() {
		this.photographer.brands = this.photographer.brands ? this.photographer.brands : [];

		let brand = this.photographer.brands?.find((x) => x.brandId == this.brands.selected!.brandId);
		if (!brand) {
			brand = new PhotographerBrand({
				brandId: this.brands.selected!.brandId,
				brandName: this.brands.selected!.brandName,
				categories: this.categories.selected
					? [
							new PhotographerCategory({
								brandId: this.brands.selected!.brandId,
								personId: this.photographer.personId,
								categoryId: this.categories.selected.id,
								categoryName: this.categories.selected.name
							})
						]
					: []
			});

			this.photographer.brands.push(brand);
			this.brands.selected = undefined;
			this.categories.selected = undefined;
		} else if (this.categories.selected && !!brand.categories.length && !brand.categories.some((x) => x.categoryId === this.categories.selected!.id)) {
			brand.categories.push(
				new PhotographerCategory({
					brandId: brand.brandId,
					personId: this.photographer.personId,
					categoryId: this.categories.selected.id,
					categoryName: this.categories.selected.name
				})
			);
			this.brands.selected = undefined;
			this.categories.selected = undefined;
		} else {
			this.#utilitiesService.showSnackbar('Addition conflicts with already present data');
		}
	}

	onSave() {
		if (this.checkFields()) {
			const form = this.form.getRawValue();
			const updatePhotographer: {
				currentUserId: number;
				companyId: number;
				photographerId?: number;
				automaticRenewal: boolean;
				brandIdCategoryId: Array<{ brandId?: number; categoryId?: number }>;
				statusId?: number;
				shotCost?: number;
				maxItems?: number;
				discountThreshold?: number;
				discountPercentage?: number;
				startDate?: string;
				endDate?: string;
			} = {
				currentUserId: this.#appService.user.userId,
				companyId: this.#appService.companyId,
				photographerId: this.photographer.personId,
				automaticRenewal: form.automaticRenewal,
				brandIdCategoryId: [],
				statusId: form.statusId,
				shotCost: form.shotCost,
				maxItems: form.maxItems,
				discountThreshold: form.discountThreshold,
				discountPercentage: form.discountPercentage,
				startDate:
					form.statusId !== this.photographer.statusId && form.statusId === this.statusIdToSetHiredData
						? parseZone(new Date()).utc(true).toISOString()
						: undefined,
				endDate: form.hiredUntil || undefined
			};

			this.photographer.brands!.forEach((b) => {
				b.categories && b.categories.length
					? b.categories.forEach((c) => updatePhotographer.brandIdCategoryId.push({ brandId: b.brandId, categoryId: c.categoryId }))
					: updatePhotographer.brandIdCategoryId.push({ brandId: b.brandId, categoryId: undefined });
			});

			this.#graphQLService
				.postData(MutationQuery.EDIT_PHOTOGRAPHER, updatePhotographer)
				.pipe(takeUntilDestroyed(this.#destroyRef))
				.subscribe({
					next: () => this.#router.navigate(['/company/photographers']),
					error: (err) => this.#utilitiesService.showSnackbar(err.errors.message)
				});
		} else {
			this.#utilitiesService.showSnackbar('Incorrect or missing fields');
		}
	}

	private getBrands() {
		this.#graphQLService
			.getData<Brand>(SharedQueries.GET_BRANDS_NO_IDS, { search: '', companyId: Number(this.#appService.companyId), first: 50, after: null })
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res) => res['data']['brands'])
			)
			.subscribe({
				next: (res: WrapperResultGraphQL<any>) => {
					this.brands.data = res.nodes.map((el) => ({
						brandId: el.brandId,
						brandName: el.brand.name,
						hasSizeGroup: el.hasSizeGroup,
						displayId: el.brandId!,
						displayName: el.brand.name
					}));
				}
			});
	}

	private getCategories() {
		this.categories.data = [];
		const filters = { search: '', companyId: this.#appService.companyId, first: null, after: null, brandIds: [this.brands.selected?.brandId] };

		this.#graphQLService
			.getData<Category>(SharedQueries.GET_CATEGORIES_NO_IDS, filters)
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res) => res['data']['category'])
			)
			.subscribe({
				next: (res: WrapperResultGraphQL<Category>) => {
					this.categories.data = res.nodes.map((el) => ({ ...el }));
				}
			});
	}

	private getPhotographerById(photographerId: number) {
		this.#graphQLService
			.getData<Photographer>(PhotographerQueries.GET_PHOTOGRAPHER_BY_ID, { photographerId: photographerId })
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res) => res['data']['photographerById'])
			)
			.subscribe({
				next: (res: WrapperResultGraphQL<Photographer>) => {
					this.photographer = res.nodes[0];
					this.setForm();
				}
			});
	}

	private getPhotographerStatus() {
		this.#graphQLService
			.getData<PhotographerStatus>(PhotographerQueries.GET_PHOTOGRAPHER_STATUS_NO_IDS)
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res: any) => res['data']['photographerStatus'])
			)
			.subscribe({
				next: (data) => {
					this.status.data = data.nodes
						.filter((s) => s.isCompanySelectable)
						.map((el) => ({ ...el, disabled: false, selected: true, displayId: el.id, displayName: el.name }));
					if (this.status.selected && !this.status.data.some((x) => x.id === this.status.selected!.id)) this.status.selected = undefined;
					this.getBrands();
				}
			});
	}

	private checkFields(): boolean {
		return (this.photographer.statusId !== this.statusIdToSetHiredData || !!this.photographer.hiredUntil) && this.photographer.maxItems > 0;
	}

	private setForm(): void {
		let hiredUntilDate: Date | null = null;
		if (this.photographer.hiredUntil) {
			const parts = this.photographer.hiredUntil.split(/[\/\s:]/);
			if (parts.length >= 3) {
				const day = parseInt(parts[0], 10);
				const month = parseInt(parts[1], 10) - 1;
				const year = parseInt(parts[2], 10);
				hiredUntilDate = new Date(year, month, day);
			}
		}

		this.form = new FormGroup({
			statusId: new FormControl<number>(this.photographer.statusId ?? 0, { validators: [Validators.required] }),
			shotCost: new FormControl<number>(this.photographer.shotCost ?? 0, { validators: [Validators.required] }),
			maxItems: new FormControl<number>(this.photographer.maxItems ?? 0, { validators: [Validators.required] }),
			discountThreshold: new FormControl<number>(this.photographer.discountThreshold ?? 0, { validators: [Validators.required] }),
			discountPercentage: new FormControl<number>(this.photographer.discountPercentage ?? 0, { validators: [Validators.required] }),
			hiredDate: new FormControl<string>(this.photographer.hiredUntil ?? '', { validators: [Validators.required] }),
			hiredUntil: new FormControl<Date | null>(hiredUntilDate, { validators: [Validators.required] }),
			automaticRenewal: new FormControl<boolean>(this.photographer.automaticRenewal ?? false, { validators: [Validators.required] }),
			brands: new FormControl<PhotographerBrand[]>(this.photographer.brands ?? [], { validators: [Validators.required] })
		});
	}
}
